import { Box, Drawer, Typography } from "@mui/material";
import { EventEditControls } from "components/manualReview/editModal/EventEditControls";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import { useReviewSaveError } from "components/manualReview/hooks/useReviewSaveError";
import { fetchErrorDetail } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useState } from "react";
import { CommentedEvent } from "src/store/api/generatedApi";
import { useReverifyCommentedPeriod } from "./useReverifyCommentedPeriod";
import { COMMENT_REVIEW_EDIT_SCOPE } from "components/manualReview/hotkeyScopes";

const DRAWER_WIDTH = 440;

const saveErrorDetail = (error: unknown): string | null => {
  const detail = fetchErrorDetail(error);
  if (detail !== null) {
    return detail;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return null;
};

export const CommentEditDrawer = ({
  open,
  onClose,
  commentedEvent,
  event,
  location,
}: {
  open: boolean;
  onClose: () => void;
  commentedEvent: CommentedEvent;
  event: ScrapEyesEvent;
  location: string;
}) => {
  const { t } = useTenantTranslation();
  const reportReviewSaveError = useReviewSaveError();
  const { reverify, isReverifying, isLoading } = useReverifyCommentedPeriod({
    commentedEvent,
    location,
    enabled: open,
  });
  const [pending, setPending] = useState<Partial<ScrapEyesEvent>>({});

  const workingEvent = { ...event, ...pending };

  const handleClose = () => {
    setPending({});
    onClose();
  };

  const handleSave = async () => {
    if (isReverifying || isLoading) {
      return;
    }
    try {
      await reverify(pending);
      handleClose();
    } catch (error) {
      const detail = saveErrorDetail(error);
      await reportReviewSaveError(error, detail ?? undefined);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box sx={{ width: DRAWER_WIDTH }}>
        <Typography variant="h6" sx={{ px: 2, pt: 2 }}>
          {t("editEvent")}
        </Typography>
        <EventEditControls
          event={workingEvent}
          location={location}
          onChange={(updates) => {
            setPending((prev) => ({ ...prev, ...updates }));
          }}
          scope={COMMENT_REVIEW_EDIT_SCOPE}
          enabled={open && !isReverifying}
          saveBusy={isLoading || isReverifying}
          onSave={() => {
            void handleSave();
          }}
        />
      </Box>
    </Drawer>
  );
};
